/* eslint-disable no-underscore-dangle */
import { network } from '@tencent/netcheck';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { MoveType } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { IMenuEventParams } from '@src/slide/application/service/menu-manager/menu-event/menu-event.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWOperationType,
    TDWModuleType,
    TDWActionType,
    TDWVer4Type,
    TDWVer9Type,
    EventCodeType,
    TDWFileType, TDWVer5Type,
    EventPosition,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import Loading from '@tencent/dui/lib/components/Loading';
import { SlideSizeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-size/slide-size.interface';
import i18n from '@i18next/i18n';
import { PropertyEditorShowAction, CommentShowAction, ShowActionSource } from '@src/js/constant';
import { IS_ENTERPRISE } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { handleAnimReportParams } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-utils';
import { copyDocLinkText } from '@src/js/common/share-utils';
import ArkLoader from '@src/js/common/ark-extensions';
import { TRANSFER_OWNERSHIP_EXTENSION } from '@src/js/common/ark-extensions/dc-components/transfer-ownership';
import { WeworkJsApi } from '@src/js/wework/jsapi/wework-js-api';
import WedriveFileManager from '@src/features/wedrive-file-manager';
import PluginManager from '@src/slide/domain/ui-model/plugin/pluginManager';
import type { CommonActionHandler } from './common.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { PopupMenuType } from '@com-pc/popup-wrapper';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { SearchReplaceType } from '@src/slide/application/service/search-replace-manager/search-replace-manager-v3.interface';
import { tdwReportForVipLinkText } from '@src/framework/frame-pc/components/vip-link-text';
import { QUICK_TOOL_AID_POSITION } from '../../../quick-tool-dialog-manager/core';
import { EShowModalSource } from '@src/slide/application/service/theme-color-manager/index.interface';
import { AddonManagerEventsKeys } from '@src/slide/application/service/addon/addon-manager.interface';
import { IFooterProps } from '@src/slide/domain/ui-model/element-modeles/property/slide-footer/slide-footer.interface';
import { openNewBlankWindow } from '@tencent/tencent_doc_open_url';
import { showImagesJoint } from '@src/js/common/ark-extensions/dc-components/toolkit-dialog';
import { REPORT_LOCATION } from '@src/slide/application/service/ai-replace-slide/ai-replace-slide-service.interface';

const getAppServices = (): IEditorAppServices => AppServiceProvider.getInstance().getAppServices() as
        IEditorAppServices;

const _actionSearchReplace = async () => {
    const searchReplaceManager = await (getAppServices() as IPcEditorAppServices).editableServiceLayer
        ?.searchReplaceManager?.promise();
    const openFlag = searchReplaceManager?.dialogOpenFlag;
    if (openFlag) {
        searchReplaceManager?.closeDialog();
    } else {
        searchReplaceManager?.openDialog(SearchReplaceType.Search);
    }
};

const _actionCut = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    const { values } = menuEventParams;
    // @ts-ignore
    const type = values?.RegionType ?? RegionType.EDITOR;
    if (![RegionType.EDITOR, RegionType.SLIDELIST].includes(type)) return;
    getAppServices().editableServiceLayer?.copyPasteManager?.dangerousInstance?.triggerCutEvent();

    const popupMenuType = typeof values === 'string' ? undefined : values?.popupMenuType;
    if ([PopupMenuType.LAYOUT_MASTER, PopupMenuType.LAYOUT_MASTER_GAP].includes(popupMenuType)) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.THUMB_CUT,
        });
    }
};

const _actionCopy = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    const { values } = menuEventParams;
    // @ts-ignore
    const type = values?.RegionType ?? RegionType.EDITOR;
    if (![RegionType.EDITOR, RegionType.SLIDELIST].includes(type)) return;
    getAppServices().editableServiceLayer?.copyPasteManager?.dangerousInstance?.triggerCopyEvent();

    const popupMenuType = typeof values === 'string' ? undefined : values?.popupMenuType;
    if ([PopupMenuType.LAYOUT_MASTER, PopupMenuType.LAYOUT_MASTER_GAP].includes(popupMenuType)) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.THUMB_COPY,
        });
    }
};

const _actionPaste = (): void => {
    getAppServices().editableServiceLayer?.copyPasteManager?.dangerousInstance?.triggerPasteEvent();
};

const _actionMoveDown = (menuManagerInstance: IMenuManager): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.moveElement(-1);
};

const _actionMoveUp = (menuManagerInstance: IMenuManager): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.moveElement(1);
};

const _actionMoveTop = (menuManagerInstance: IMenuManager): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.setCurrentElementToFrontOrBack(MoveType.FRONT);
};

const _actionMoveBottom = (menuManagerInstance: IMenuManager): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.setCurrentElementToFrontOrBack(MoveType.BACK);
};

const _actionOpenSelectWindow = (menuManagerInstance: IMenuManager): void => {
    menuManagerInstance.rightSideBarService?.hide();
    menuManagerInstance.rightSideBarManager?.setIsSelectWindowShown(true);
};

const _actionSelectAll = (menuManagerInstance: IMenuManager): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    const currentSelectedModel = menuManagerInstance.selectionServiceRef.getSelectedModel();
    switch (menuManagerInstance.selectionServiceRef.lastMouseDownRegion) {
        case RegionType.SLIDELIST:
            menuManagerInstance.uiDataServiceRef.presentation.selectAll();
            break;
        case RegionType.EDITOR:
            if (
                (getAppServices().viewableServiceLayer.editorInstanceService.checkEditorIsEditing()
                    || getAppServices().viewableServiceLayer.editingStateManager.isEditing)
                && currentSelectedModel?.type === UIModelType.SHAPE
                // @ts-ignore
                && currentSelectedModel?.editorInstance
            ) {
                // 【【ppt】文本编辑态，菜单编辑-全选，应该选中当前shape所有文本，不应该选中所有shape】http://tapd.oa.com/TIM_web/bugtrace/bugs/view?bug_id=1010143861073232935
                // @ts-ignore
                currentSelectedModel?.editorInstance.setSelectionAll();
            } else {
                currentSlide?.selectAll();
            }
            break;
    }
};

const _actionAddGroup = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.groupElements();
    const { menuType, actionType, values, extraOption } = menuEventParams;
    menuManagerInstance.report(menuType, actionType);
};

const _actionConvertToGroup = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    // 转组合
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.convertToGroup();
};

const _actionRemoveGroup = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    const { currentSlide } = menuManagerInstance.selectionServiceRef;
    currentSlide?.unGroupElements();
    const { menuType, actionType, values, extraOption } = menuEventParams;
    menuManagerInstance.report(menuType, actionType);
};

const _actionCreateSlide = (menuManagerInstance: IMenuManager): void => {
    const { slideClearanceIndex } = menuManagerInstance.selectionServiceRef;
    menuManagerInstance.uiDataServiceRef.presentation.addDefaultSlide();
    menuManagerInstance.getSectionManagerDelegate()?.expandCurrentSection(slideClearanceIndex);
};

const _actionCopySlide = (menuManagerInstance: IMenuManager) => {
    if (menuManagerInstance.selectionServiceRef.selectedSlides.length === 0) {
        menuManagerInstance.uiDataServiceRef.presentation
            .duplicateSlide([menuManagerInstance.selectionServiceRef.currentSlide]);
    } else {
        menuManagerInstance.uiDataServiceRef.presentation.duplicateSlide();
    }
};

const _actionDeleteSlide = (menuManagerInstance: IMenuManager) => {
    if (menuManagerInstance.selectionServiceRef.selectedSlides.length === 0) {
        menuManagerInstance.uiDataServiceRef.presentation
            .deleteSlide([menuManagerInstance.selectionServiceRef.currentSlide]);
    } else {
        menuManagerInstance.uiDataServiceRef.presentation.deleteSlide();
    }
};

const _actionSlidePlay = (menuManagerInstance: IMenuManager) => {
    getAppServices().viewableServiceLayer.playManager.playAction?.(true);
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_LOCAL_PLAY,
        action: TDWActionType.RIGHT_CLICK,
    });
};

const _actionSlideWide = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { actionType } = menuEventParams;
    const slideSizeType = actionType === ActionType.SLIDE_WIDE
        ? SlideSizeType.SLIDE_SIZE_16_9 : SlideSizeType.SLIDE_SIZE_4_3;
    if (slideSizeType === SlideSizeType.SLIDE_SIZE_16_9) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_EDIT_PAGE,
            action: TDWActionType.CHANGE_SN,
        });
    } else if (slideSizeType === SlideSizeType.SLIDE_SIZE_4_3) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_EDIT_PAGE,
            action: TDWActionType.CHANGE_FT,
        });
    }
    const load = Loading.show({ tip: i18n.t('切换中...') });
    setTimeout(() => {
        menuManagerInstance.uiDataServiceRef.presentation.updateSlideSizeType(slideSizeType);
        load.close();
    }, 20);
};

/**
 * 插入第三方内容
 * @param {String} type 组件类型
 * @param {String} id 组件id
 * @param {String} menuId 插件 menu item id，与插件SDK交互
 */
const _actionInsertIframe = async (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { actionType, values } = menuEventParams;
    if (!network.isOnline) {
        getAppServices().viewableServiceLayer.addonManager.event.emit(AddonManagerEventsKeys.ERROR_NETWORK);
        return;
    }

    if (typeof values !== 'string') return;
    // 获取插件 aid
    const aid = menuManagerInstance.getAddonID(values);
    // 调用插件插入接口
    menuManagerInstance.addonInsertHandler(values);
    // 新功能引导
    const iframeType = values?.[values.length - 1];
    localStorage.setItem(`${actionType}-${iframeType}_red_point`, 'false');
    localStorage.setItem('insert-iframe_red_point', 'false');
    const reportId = getAppServices().viewableServiceLayer.addonManager.getAddonReportId(aid, values);
    const name = getAppServices().viewableServiceLayer.addonManager.getAddonReportName(aid, values);
    // 上报
    tdw({
        opername: TDWOperationType.DOC_ADDON_PLATFORM,
        module: TDWModuleType.THIRD_SERVICES,
        action: TDWActionType.ADDON_INSERT,
        ver6: aid,
        ver10: reportId,
    });

    const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_TOOLBAR_INSERT,
        action: TDWActionType.ADDON,
        ver3,
        ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
        ver5: name,
    });
};

/**
 * 在正文内容中插入腾讯文档文件的超链接
 */
const actionInsertTencentDoc = (menuManagerInstance: IMenuManager) => {
    // 调用腾讯文档选择器
    menuManagerInstance.insertTdocManager?.promise().then((insertTdocManager) => {
        insertTdocManager.show();
        // 点击上报
        tdw({
            opername: TDWOperationType.DOC_INSERT_TDOC,
            module: TDWModuleType.TDOC_SELECTOR,
            action: TDWActionType.INSERT_TDOC_ENTER_CLK,
        });
        const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TOOLBAR_INSERT,
            action: TDWActionType.TDOC,
            ver3,
            ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
        });
    });
};

const _actionRestartDemo = () => {
    getAppServices().viewableServiceLayer.playManager.playAction?.(false);
};

const _actionTailorPicNormal = () => {
    getAppServices().viewableServiceLayer.editingStateManager.updateElementCropState(true);
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_OBJ,
        action: TDWActionType.IMG_CUT,
    });
};

// eslint-disable-next-line no-underscore-dangle
const _actionShapeFormat = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void => {
    const { actionType } = menuEventParams;
    const showActionMap = {
        [ActionType.PIC_FORMAT]: PropertyEditorShowAction.IMAGE_TOGGLE, // 图片格式，打开格式设置侧边栏，默认聚焦图片
        [ActionType.TRANSPARENCY]: PropertyEditorShowAction.IMAGE_TOGGLE, // 工具栏图片透明度，打开格式设置侧边栏，默认聚焦图片
        [ActionType.SHAPE_FORMAT]: PropertyEditorShowAction.SHAPE_TOOGLE, // 形状格式，打开格式设置侧边栏，默认聚焦样式
        [ActionType.SET_BG_FORMAT]: PropertyEditorShowAction.SHAPE_TOOGLE, // 从右键设置背景打开格式侧边栏
        [ActionType.CHANGE_BG]: PropertyEditorShowAction.SHAPE_TOOGLE, // 从工具栏页面设置-设置背景打开格式侧边栏
        [ActionType.CHART_FORMAT]: PropertyEditorShowAction.CHART_TOGGLE,
    };
    if (showActionMap[actionType]) {
        menuManagerInstance
            .rightSideBarManager?.setPropertyEditorShowAction(showActionMap[actionType]);
    }
    menuManagerInstance.rightSideBarManager?.setIsPropertyEditorShown(true);
    menuManagerInstance.rightSideBarService?.hide();                                    // 关闭权限侧边栏
};

const _actionChangeBg = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    menuManagerInstance.selectionServiceRef.selectedElements.clear();   // 清除元素选中
    _actionShapeFormat(menuManagerInstance, menuEventParams);

    const popupMenuType = typeof menuEventParams.values === 'string' ? undefined : menuEventParams.values?.popupMenuType;
    if ([PopupMenuType.LAYOUT_MASTER, PopupMenuType.LAYOUT_MASTER_GAP].includes(popupMenuType)) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.THUMB_OPEN_SIDEBAR,
        });
    }
};


const _actionUndo = () => {
    getAppServices().editableServiceLayer?.undoRedoService.undo();
    const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_TOOLBAR,
        action: TDWActionType.UNDO,
        ver3,
        ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
    });
};

const _actionRedo = () => {
    getAppServices().editableServiceLayer?.undoRedoService.redo();
    const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_TOOLBAR,
        action: TDWActionType.REDO,
        ver3,
        ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
    });
};

const _actionInsertAnimation = (menuManagerInstance: IMenuManager) => {
    menuManagerInstance.rightSideBarManager?.setIsAnimationEditorShown(true);
    menuManagerInstance.rightSideBarService?.hide();                                    // 关闭权限侧边栏
    menuManagerInstance.selectionServiceRef.currentSlide?.convertAnimDataSdkToUi();
    const timingSdkData = menuManagerInstance.selectionServiceRef?.currentSlide?.timing;
    if (!timingSdkData) return true;
    const animReportParams = handleAnimReportParams(timingSdkData);
    tdw({
        opername: TDWOperationType.PPT_ANIMATION,
        module: TDWModuleType.TIME_NODE,
        action: TDWActionType.TOOLBAR_ANIM_CLICK,
        ver6: JSON.stringify(animReportParams),
    });
};

const actionInsertLink = (menuManagerInstance: IMenuManager) => {
    const model = menuManagerInstance.selectionServiceRef.getSelectedModel();
    if (model?.type !== UIModelType.EMPTY) { // 清空选中元素
        menuManagerInstance.selectionServiceRef.selectedElements.clear();
    }
    getAppServices().viewableServiceLayer.linkManager.insertTextWithLink();
    menuManagerInstance.selectionServiceRef.lastMouseDownRegion = RegionType.EDITOR;
    const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_TOOLBAR_INSERT,
        action: TDWActionType.LINK,
        ver3,
        ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
    });
};

/**
 * 插入Latex公式
 */
const actionInsertLatex = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { actionType, values } = menuEventParams;
    // 清除所有元素的选中态，以便重新插入公式而不是编辑公式
    // 同时可以防止替换普通图片
    menuManagerInstance.selectedMgrRef.clear();

    if (!network.isOnline) {
        getAppServices().viewableServiceLayer.addonManager.event.emit(AddonManagerEventsKeys.ERROR_NETWORK);
        return;
    }

    if (typeof values !== 'string') return;

    // 调用插件插入接口
    menuManagerInstance.addonInsertHandler(values);

    // 取消显示插入Latex公式的新功能引导
    localStorage.setItem(`${actionType}_red_point`, 'false');

    // 上报：点击顶部工具栏 - 插入Latex公式
    tdw({
        opername: TDWOperationType.DOC_FORMULA,
        module: TDWModuleType.FORMULA_TOOLBAR,
        action: TDWActionType.FORMULA_CLICK,
    });

    const ver3 = getAppServices().viewableServiceLayer.toolbarStateManager.getToolbarMode();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.PC_TOOLBAR_INSERT,
        action: TDWActionType.LATEX,
        ver3,
        ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
    });
};

/**
 * 编辑Latex公式
 */
const actionReplaceLatex = async (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { menuType, actionType } = menuEventParams;
    menuManagerInstance.actionReplaceLatex(menuType, actionType);
};

// 导入本地幻灯片选页
const actionImportLocalSlide = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    document.getElementById('import-slide-input')?.click();
    tdw({
        opername: TDWOperationType.PAGING_SELECTION,
        module: TDWModuleType.SLIDE_UPLOAD,
        action: TDWActionType.RIGHT_CLICK_IMPORT,
    });
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.INSERT_SLIDE_ENTRY,
        action: TDWActionType.INSERT_SLIDE_ENTRY_LOCAL,
        // @ts-ignore
        ver5: values?.TriggerFrom,
    });
};

// 导入在线幻灯片选页
const actionImportOnlineSlide = async (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    const importOnlineFileManager = await menuManagerInstance.importOnlineFileManager?.promise();
    importOnlineFileManager?.show();
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.INSERT_SLIDE_ENTRY,
        action: TDWActionType.INSERT_SLIDE_ENTRY_ONLINE,
        // @ts-ignore
        ver5: values?.TriggerFrom,
    });
};

// 导入幻灯片二级菜单显示
const handleImportSlideMenuShow = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    // @ts-ignore
    if (values?.visible) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.INSERT_SLIDE_ENTRY,
            action: TDWActionType.INSERT_SLIDE_ENTRY_CLICK,
            // @ts-ignore
            ver5: values?.TriggerFrom,
        });
    }
};

/**
 * 插入非官方插件内容
 */
const actionInsertAddonUnOfficial = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    if (!network.isOnline) {
        getAppServices().viewableServiceLayer.addonManager.event.emit(AddonManagerEventsKeys.ERROR_NETWORK);
        return;
    }

    if (typeof values !== 'string') return;

    // FIXME: 产品定义字段不规范，等插件能力通用后修正
    tdw({
        opername: TDWOperationType.PLUG_IN,
        module: TDWModuleType.PPT,
        action: TDWActionType.ISLIDE,
    });

    // 调用插件插入接口
    menuManagerInstance.addonInsertHandler(values);
};

const handleAddonSubMenuShow = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    // @ts-ignore
    if (values?.visible) {
        let ver9;
        switch (menuManagerInstance.selectionServiceRef.getSelectedModel()?.type) {
            case UIModelType.SHAPE:
                ver9 = TDWVer9Type.DETAIL_IFRAME_RIGHT_CLK;
                break;
            case UIModelType.IMAGE:
                ver9 = TDWVer9Type.DETAIL_PIC_RIGHT_CLK;
                break;
            case UIModelType.EMPTY:
                ver9 = TDWVer9Type.DETAIL_TXT_RIGHT_CLK;
                break;
            default:
                break;
        }
        tdw({
            opername: TDWOperationType.DOC_ADDON,
            module: TDWModuleType.ADDON_LIST,
            action: TDWActionType.LIST_VIEW,
            eventcode: EventCodeType.DOC_ADDON_ADDON_LIST_LIST_VIEW,
            ver9,
            file_type: TDWFileType.SLIDE,
        });
    }
};

const _actionTransferOwnership = async () => {
    // 接入dc组件
    const transferOwnership = await ArkLoader.extensionService.getExtension(TRANSFER_OWNERSHIP_EXTENSION.name);
    transferOwnership.show({
        docId: window.clientVars?.globalPadId, // 文档id
        corpId: window.clientVars?.creatorCorpId, // 创建者的公司id
        disabledUserIds: [window.clientVars?.userInfo?.uid],
    });
};

// 切换编辑模式
const actionChangeEditType = () => {
    (getAppServices() as IPcEditorAppServices).editableServiceLayer
        ?.masterLayoutEditService.changeEditType(EDIT_TYPE.MASTER_LAYOUT);
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.MASTER,
        action: TDWActionType.MASTER_CLICK,
    });
};

// 插入版式
const insertLayout = (menuManagerInstance: IMenuManager) => {
    const presentation = menuManagerInstance.selectionServiceRef.currentSlide?.presentationRef;
    presentation?.addLayout();
    // 侧边栏插入版式上报，复用函数的时候注意上报内容是否准确
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.MASTER,
        action: TDWActionType.THUMB_INSERT_LAYOUT,
    });
};

// 复制版式
const copyLayout = (menuManagerInstance: IMenuManager) => {
    const presentation = menuManagerInstance.selectionServiceRef.currentSlide?.presentationRef;
    presentation?.duplicateSlide();
    // 侧边栏版式上报，复用函数的时候注意上报内容是否准确
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.MASTER,
        action: TDWActionType.THUMB_COPY_LAYOUT,
    });
};

// 删除版式
const deleteLayout = (menuManagerInstance: IMenuManager) => {
    const presentation = menuManagerInstance.selectionServiceRef.currentSlide?.presentationRef;
    presentation?.deleteSlide();
    // 侧边栏版式上报，复用函数的时候注意上报内容是否准确
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.MASTER,
        action: TDWActionType.THUMB_DELETE_LAYOUT,
    });
};

// 主题字体改变
const handlerFontThemeChange = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    menuManagerInstance.uiDataServiceRef.presentation.updateThemeFontFamily(values);
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.MASTER,
        action: TDWActionType.SET_THEME_FONT,
    });
};

// 占位符格式变更
const handlerPlaceholderFormat = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    const { values } = menuEventParams;
    menuManagerInstance.selectionServiceRef.currentSlide?.updatePlaceholderFormat(values as IFooterProps);
};

const handleAddSection = (menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams) => {
    if (!menuEventParams.values || typeof menuEventParams.values === 'string') {
        return;
    }
    let { gapIndex } = menuEventParams.values;
    if (typeof gapIndex !== 'number') {
        return;
    }
    if (menuManagerInstance.selectionServiceRef.slideClearanceIndex === -1) {
        // 选中的是某一页而不是间隙，使用 gapIndex - 1 作为间隙索引来添加节
        gapIndex -= 1;
    }
    const oldSectionListLength = menuManagerInstance.uiDataServiceRef.presentation.sectionList?.length ?? 0;
    menuManagerInstance.uiDataServiceRef
        .presentation.getSectionProperty().addNewSection(gapIndex, i18n.t('无标题节'));
    const newSectionListLength = menuManagerInstance.uiDataServiceRef.presentation.sectionList?.length ?? 0;
    menuManagerInstance.getSectionManagerDelegate()?.addUnfoldedSectionStateAt(
        gapIndex,
        newSectionListLength - oldSectionListLength,
    );
};

const deleteSections = (
    menuManagerInstance: IMenuManager,
    menuEventParams: IMenuEventParams,
    deleteSlides: boolean,
) => {
    if (!menuEventParams.values || typeof menuEventParams.values === 'string') {
        return;
    }
    const { sectionIndex } = menuEventParams.values;
    menuManagerInstance.uiDataServiceRef
        .presentation.getSectionProperty().deleteSection(sectionIndex, deleteSlides);
    menuManagerInstance.getSectionManagerDelegate()?.deleteSectionStateAt(sectionIndex);
};

const handleRenameSection: CommonActionHandler = (menuManagerInstance: IMenuManager, menuEventParams) => {
    if (!menuEventParams.values || typeof menuEventParams.values === 'string') {
        return;
    }
    const { sectionIndex } = menuEventParams.values;
    const sectionManager = menuManagerInstance.getSectionManagerDelegate();
    if (sectionManager) {
        sectionManager.nameEditingIndex = sectionIndex;
    }
};

const handleDeleteSections: CommonActionHandler = (...args) => {
    deleteSections(...args, false);
};

const handleDeleteSectionAndSlides: CommonActionHandler = (...args) => {
    deleteSections(...args, true);
};

const handleDeleteAllSections: CommonActionHandler = (menuManagerInstance: IMenuManager) => {
    menuManagerInstance.uiDataServiceRef.presentation.getSectionProperty().deleteAllSections();
    menuManagerInstance.getSectionManagerDelegate()?.deleteAllSectionsState();
};

const handleMoveSectionUp: CommonActionHandler = (menuManagerInstance: IMenuManager, menuEventParams) => {
    if (!menuEventParams.values || typeof menuEventParams.values === 'string') {
        return;
    }
    const { sectionIndex } = menuEventParams.values;
    const sectionProperty = menuManagerInstance.uiDataServiceRef.presentation.getSectionProperty();
    sectionProperty.moveSectionUp(sectionIndex);
    const [, beginIndex] = sectionProperty.getSectionSlideModelsAndBeginSlideIndex(sectionIndex - 1);
    menuManagerInstance.getSectionManagerDelegate()?.selectSection(sectionIndex - 1, beginIndex);
};

const handleMoveSectionDown: CommonActionHandler = (menuManagerInstance: IMenuManager, menuEventParams) => {
    if (!menuEventParams.values || typeof menuEventParams.values === 'string') {
        return;
    }
    const { sectionIndex } = menuEventParams.values;
    const sectionProperty = menuManagerInstance.uiDataServiceRef.presentation.getSectionProperty();
    sectionProperty.moveSectionDown(sectionIndex);
    const [, beginIndex] = sectionProperty.getSectionSlideModelsAndBeginSlideIndex(sectionIndex + 1);
    menuManagerInstance.getSectionManagerDelegate()?.selectSection(sectionIndex + 1, beginIndex);
};

const handleCollapseAllSections: CommonActionHandler = (menuManagerInstance: IMenuManager) => {
    menuManagerInstance.getSectionManagerDelegate()?.collapseAllSections();
};

const handleExpandAllSections: CommonActionHandler = (menuManagerInstance: IMenuManager) => {
    menuManagerInstance.getSectionManagerDelegate()?.expandAllSections();
};

const handleHideSlide: CommonActionHandler = (menuManager: IMenuManager) => {
    const selectedSlides = menuManager.selectionServiceRef.selectedSlides.selectedArray;
    menuManager.uiDataServiceRef.presentation.toggleShowSlides(selectedSlides, false);
};

const handleCancelHideSlide: CommonActionHandler = (menuManager: IMenuManager) => {
    const selectedSlides = menuManager.selectionServiceRef.selectedSlides.selectedArray;
    menuManager.uiDataServiceRef.presentation.toggleShowSlides(selectedSlides, true);
};

/**
 * 融合会议内转发微信会话
 */
const forwardDocInTencentMeeting = () => {
    WeworkJsApi.wwappDocOnOpenMenu({
        menuType: 2,
        from: 'menubar',
    });
};

const handleDisplayTextRuler = (menuManager: IMenuManager) => {
    menuManager.textRulerManager?.updateDisplayRuler();
};

const handleDisplayGuides = (menuManager: IMenuManager) => {
    menuManager.guidesManager?.changeDisplayGuides();
};

const handleToggleSnapLine = (menuManager: IMenuManager) => {
    menuManager.snapLineService?.toggleSnapLine();
};

const handleAddVertGuide = (menuManager: IMenuManager, params) => {
    if (!params?.values?.model) {
        menuManager.guidesManager?.setCurrentGuide(undefined);
    }
    menuManager.guidesManager?.addVertGuide();
};

const handleQuickTool = (params) => {
    const { actionType } = params;
    (getAppServices() as IPcEditorAppServices)
        .editableServiceLayer?.quickToolDialogManager.promise().then((quickToolDialogManager) => {
            quickToolDialogManager.showDialog(actionType.slice(4), QUICK_TOOL_AID_POSITION.RIGHT_MENU);
        });
};

const handleImagesJoint = () => {
    tdwReportImagesJointClick();
    showImagesJoint({
        aidPosition: 'rightmenu',
        fromPage: 'pc_rightmenu',
    });
};

const handleChangeTheme = () => {
    getAppServices().editableServiceLayer?.themeColorManager?.showThemeModal({
        source: EShowModalSource.rightMenu,
    });
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.COLOR_SWITCH,
        action: TDWActionType.THEME_CLICK,
        ver5: TDWVer5Type.RIGHT_MENU_SOURCE,
    });
};

const handleQuickToolShow = (params) => {
    if (params?.values?.visible) {
        tdwReportForVipLinkText();
        tdwReportImagesJointShow();
    }
};

const handleThemeEdit = (menuManager: IMenuManager) => {
    menuManager.themeColorManager?.updateThemeColorCustomDialogVisible(true);
};

const handleThemeDelete = (menuManager: IMenuManager) => {
    menuManager.themeColorManager?.removeCustomThemeByCurrentEditKey();
};

const handleAddHorzGuide = (menuManager: IMenuManager, params) => {
    if (!params?.values?.model) {
        menuManager.guidesManager?.setCurrentGuide(undefined);
    }
    menuManager.guidesManager?.addHorzGuide();
};

const handleDeleteVertGuide = (menuManager, menuEventParams) => {
    const { values } = menuEventParams;
    menuManager.guidesManager?.deleteGuide(values.model);
};

const handleDisplayGridLine = (menuManager) => {
    menuManager.gridLineManager?.changeDisplayGridLine();
};

const handleChangeIsGridAlign = (menuManager) => {
    menuManager.gridLineManager?.changeIsGridAlign();
};

const handleChangeGridDialogVisible = (menuManager) => {
    if (menuManager.gridLineManager) {
        menuManager.gridLineManager.displayGridLineDialog = true;
    }
    tdw({
        opername: TDWOperationType.DOC_PPT,
        module: TDWModuleType.GRID_LINE_SETTING,
        action: TDWActionType.OPEN_GRID_LINE_DIALOG,
    });
};

function tdwReportImagesJointShow() {
    const vipInfo = ArkLoader.extensionService.dependentServiceCombinder?.accountApplication?.vipInfo;
    tdw({
        opername: TDWOperationType.DOC_VIP_EDITOR,
        module: TDWModuleType.RIGHT_MENU,
        action: TDWActionType.IMAGE_JOINT_HOVER,
        ver5: vipInfo.type,
        ver9: EventPosition.PC_RIGHT_MENU,
        ver12: 'slide',
    });
}

function tdwReportImagesJointClick() {
    const vipInfo = ArkLoader.extensionService.dependentServiceCombinder?.accountApplication?.vipInfo;
    tdw({
        opername: TDWOperationType.DOC_VIP_EDITOR,
        module: TDWModuleType.RIGHT_MENU,
        action: TDWActionType.IMAGE_JOINT_CLICK,
        ver5: vipInfo.type,
        ver9: EventPosition.PC_RIGHT_MENU,
        ver12: 'slide',
    });
}


const watermarkShowDialog = () => {
    getAppServices().viewableServiceLayer.watermarkManager.promise().then((watermarkManager) => {
        watermarkManager.showDialog();
    });
};

const handleOpenSourceUrl = (menuManager) => {
    try {
        const model = menuManager.selectionServiceRef?.getSelectedModel();
        const descr = model.sdkData?.getDescription();
        if (descr?.sourceUrl) {
            openNewBlankWindow(descr.sourceUrl);
        }
    } catch (e) {
        window.log?.error('open source url error', e);
        return;
    }
};

const handleInsertComment = () => {
    try {
        (getAppServices() as IPcEditorAppServices).viewableServiceLayer.commentManager?.promise()
            .then((commentManager) => {
                commentManager.setCommentShowAction(CommentShowAction.INSERT_COMMENT, ShowActionSource.RIGHT_MENU);
            });
    } catch (e) {
        window.log?.error('insert comment error', e);
        return;
    }
};

const handleMoreSetting = (menuManagerInstance) => {
    menuManagerInstance.rightSideBarManager?.setIsPropertyEditorShown(true);
};

const handleAIReplaceSlide = (params?: IMenuEventParams) => {
    const region = (params?.values as any)?.RegionType;
    const position = document.querySelector(`li[data-menu-item-id=${params?.actionType}]`)?.getBoundingClientRect();
    const location = {
        [RegionType.SLIDELIST]: REPORT_LOCATION.slideListRight,
        [RegionType.EDITOR]: REPORT_LOCATION.slideRight,
    }[region];

    (getAppServices() as IPcEditorAppServices).editableServiceLayer?.aiReplaceSlidePCService
        .promise()
        .then((service) => {
            if (region === RegionType.EDITOR && position) {
                service.showModal({ location, position });
            } else {
                service.showModal({ location });
            }
        });
};

/**
 * 事件列表（主要逻辑从src/js/manager/menu-manager/index.js迁移而来）
 */
const menuEventMap = new Map<string, CommonActionHandler>([
    [ActionType.SEARCH_REPLACE, () => _actionSearchReplace()],
    [ActionType.CUT, (menuManager, menuEventParams) => _actionCut(menuManager, menuEventParams)],
    [ActionType.COPY, (menuManager, menuEventParams) => _actionCopy(menuManager, menuEventParams)],
    [ActionType.CONVERT_TO_GROUP,
        (menuManager, menuEventParams) => _actionConvertToGroup(menuManager, menuEventParams)],
    [ActionType.PASTE, () => _actionPaste()],
    [ActionType.MOVE_ITEM_DOWN, menuManager => _actionMoveDown(menuManager)],
    [ActionType.MOVEDOWN, menuManager => _actionMoveDown(menuManager)],
    [ActionType.MOVE_ITEM_UP, menuManager => _actionMoveUp(menuManager)],
    [ActionType.MOVEUP, menuManager => _actionMoveUp(menuManager)],
    [ActionType.ROOT_UPPER, menuManager => _actionMoveTop(menuManager)],
    [ActionType.MOVETOP, menuManager => _actionMoveTop(menuManager)],
    [ActionType.BASE_DOWN, menuManager => _actionMoveBottom(menuManager)],
    [ActionType.SELECT_WINDOW, menuManager => _actionOpenSelectWindow(menuManager)],
    [ActionType.MOVEBOTTOM, menuManager => _actionMoveBottom(menuManager)],
    [ActionType.SELECT_ALL, menuManager => _actionSelectAll(menuManager)],
    [ActionType.ADDGROUP, (menuManager, menuEventParams) => _actionAddGroup(menuManager, menuEventParams)],
    [ActionType.REMOVEGROUP, (menuManager, menuEventParams) => _actionRemoveGroup(menuManager, menuEventParams)],
    [ActionType.NEW_SLIDE, menuManager => _actionCreateSlide(menuManager)],
    [ActionType.CREATE_SLIDE, menuManager => _actionCreateSlide(menuManager)],
    [ActionType.COPY_SLIDE, menuManager => _actionCopySlide(menuManager)],
    [ActionType.DELETE_SLIDE, menuManager => _actionDeleteSlide(menuManager)],
    [ActionType.SLIDE_PLAY, menuManager => _actionSlidePlay(menuManager)],
    [ActionType.SLIDE_STANDARD, (menuManager, menuEventParams) => _actionSlideWide(menuManager, menuEventParams)],
    [ActionType.SLIDE_WIDE, (menuManager, menuEventParams) => _actionSlideWide(menuManager, menuEventParams)],
    [ActionType.SLIDE_CUSTOM, () => true],
    [ActionType.INSERT_IFRAME_ITEM, (menuManager, params) => _actionInsertIframe(menuManager, params)],
    [ActionType.INSERT_TENCENT_DOC_ID, menuManager => actionInsertTencentDoc(menuManager)],
    [ActionType.SLIDE_HEADER_FOOTER, () => true],
    [ActionType.BROWSE_RECORD, menuManager => menuManager._actionBrowseRecord()],
    [ActionType.EXIT_DOCUMENT, menuManager => menuManager._actionExitDocument()],
    [ActionType.RESTART_DEMO, () => _actionRestartDemo()],
    [ActionType.CUR_PAGE_DEMO, menuManager => _actionSlidePlay(menuManager)],
    [ActionType.TAILOR_PIC_NORMAL, () => _actionTailorPicNormal()],
    [ActionType.CHANGE_BG, (menuManager, menuEventParams) => _actionChangeBg(menuManager, menuEventParams)],
    [ActionType.SET_BG_FORMAT, (menuManager, menuEventParams) => _actionChangeBg(menuManager, menuEventParams)],
    [ActionType.SHAPE_FORMAT, (menuManager, menuEventParams) => _actionShapeFormat(menuManager, menuEventParams)],
    [ActionType.CHART_FORMAT, (menuManager, menuEventParams) => _actionShapeFormat(menuManager, menuEventParams)],
    [ActionType.TRANSPARENCY, (menuManager, menuEventParams) => _actionShapeFormat(menuManager, menuEventParams)],
    [ActionType.EDIT_FORMAT, (menuManager, menuEventParams) => _actionShapeFormat(menuManager, menuEventParams)],
    [ActionType.PIC_FORMAT, (menuManager, menuEventParams) => _actionShapeFormat(menuManager, menuEventParams)],
    [ActionType.REVISION_RECORD, menuManager => menuManager._actionRevisionRecord()],
    [ActionType.NATIVE_PPT, menuManager => menuManager._actionNativePPT()],
    [ActionType.SHOW_RATIO, menuManager => menuManager.showRatioView()],
    [ActionType.UNDO, () => _actionUndo()],
    [ActionType.REDO, () => _actionRedo()],
    [ActionType.MOVE_SLIDE_UP, menuManager => menuManager.uiDataServiceRef.presentation.moveSlides()],
    [ActionType.PERFORMANCE, menuManager => menuManager._actionPerformance()],
    [ActionType.INSERT_ANIMATION, menuManager => _actionInsertAnimation(menuManager)],
    [ActionType.LINK, menuManager => menuManager._actionLink()],
    [ActionType.PARAGRAPH_MORE, menuManager => menuManager.paragraphManager?.showParagraph()],
    [ActionType.WATERMARK, () => watermarkShowDialog()],
    [ActionType.EXPORT_PDF, menuManager => menuManager.exportPDF()],
    [ActionType.INSERT_WITH_LINK, menuManager => actionInsertLink(menuManager)],
    [ActionType.INSERT_WITH_LATEX, (menuManager, menuEventParams) => actionInsertLatex(menuManager, menuEventParams)],
    [ActionType.REPLACE_WITH_LATEX, (menuManager, menuEventParams) => actionReplaceLatex(menuManager, menuEventParams)],
    [ActionType.OCR_WITH_LATEX, (menuManager, menuEventParams) => actionReplaceLatex(menuManager, menuEventParams)],
    [ActionType.PERMISSION_SETTING,
        () => getAppServices()?.viewableServiceLayer.extensionService?.getCallBackAdapter()?.onAuthClick?.()],
    [ActionType.ADVANCED_PERMISSION,
        () => getAppServices().editableServiceLayer?.advancedPermissionManager?.showDialog()],
    [ActionType.SET_WATER_MARK, () => watermarkShowDialog()],
    [ActionType.IMPORT_LOCAL_SLIDE, (menuManager, params) => actionImportLocalSlide(menuManager, params)],
    [ActionType.IMPORT_ONLINE_SLIDE, (menuManager, params) => actionImportOnlineSlide(menuManager, params)],
    [ActionType.TEXT_FORMAT, menuManager => menuManager.actionTextFormat()],
    [ActionType.ADDON_UNOFFICIAL, (menuManager, params) => actionInsertAddonUnOfficial(menuManager, params)],
    [ActionType.COPY_DOC_LINK, copyDocLinkText],
    [ActionType.IMPORT_SLIDE, (menuManager, params) => handleImportSlideMenuShow(menuManager, params)],
    [ActionType.ADDON, (menuManager, params) => handleAddonSubMenuShow(menuManager, params)],
    [ActionType.FONT_THEME, (menuManager, params) => handlerFontThemeChange(menuManager, params)],
    [ActionType.PLACEHOLDER_FORMAT, (menuManager, params) => handlerPlaceholderFormat(menuManager, params)],
    [ActionType.MASTER_LAYOUT, () => actionChangeEditType()],
    [ActionType.INSERT_LAYOUT, MenuManager => insertLayout(MenuManager)],
    [ActionType.COPY_LAYOUT, MenuManager => copyLayout(MenuManager)],
    [ActionType.DELETE_LAYOUT, MenuManager => deleteLayout(MenuManager)],
    [ActionType.ADD_SECTION, (menuManager, params) => handleAddSection(menuManager, params)], // 添加节
    [ActionType.RENAME_SECTION, (menuManager, params) => handleRenameSection(menuManager, params)], // 重命名节
    [ActionType.DELETE_SECTION, (menuManager, params) => handleDeleteSections(menuManager, params)], // 删除节
    // 删除节以及节下所有幻灯片
    [ActionType.DELETE_SECTION_AND_SLIDES, (menuManager, params) => handleDeleteSectionAndSlides(menuManager, params)],
    [ActionType.DELETE_ALL_SECTIONS, (menuManager, params) => handleDeleteAllSections(menuManager, params)], // 删除所有节
    [ActionType.MOVE_SECTION_UP, (menuManager, params) => handleMoveSectionUp(menuManager, params)], // 上移节
    [ActionType.MOVE_SECTION_DOWN, (menuManager, params) => handleMoveSectionDown(menuManager, params)], // 下移节
    [ActionType.COLLAPSE_ALL_SECTIONS, (instance, params) => handleCollapseAllSections(instance, params)], // 折叠所有节
    [ActionType.EXPAND_ALL_SECTIONS, (menuManager, params) => handleExpandAllSections(menuManager, params)], // 展开所有节
    [ActionType.HIDE_SLIDE, (menuManager, params) => handleHideSlide(menuManager, params)],
    [ActionType.CANCEL_HIDE_SLIDE, (menuManager, params) => handleCancelHideSlide(menuManager, params)],
    [ActionType.DISPLAY_GUIDES, (menuManager, params) => handleDisplayGuides(menuManager)], // 展示/隐藏参考线
    [ActionType.DISPLAY_TEXT_RULER, (menuManager, params) => handleDisplayTextRuler(menuManager)], // 展示/隐藏标尺
    [ActionType.TOGGLE_SNAP_LINE, (menuManager, params) => handleToggleSnapLine(menuManager)],  // 开启/关闭智能吸附
    [ActionType.ADD_HORZ_GUIDE, (menuManager, params) => handleAddHorzGuide(menuManager, params)], // 新增水平参考线
    [ActionType.ADD_VERT_GUIDE, (menuManager, params) => handleAddVertGuide(menuManager, params)], // 展开垂直参考线
    [ActionType.DELETE_GUIDE, (menuManager, params) => handleDeleteVertGuide(menuManager, params)], // 删除参考线
    [ActionType.DISPLAY_GRID_LINE, (menuManager, params) => handleDisplayGridLine(menuManager)], // 展示/隐藏网格线
    [ActionType.GRID_LINE_ALIGN, (menuManager, params) => handleChangeIsGridAlign(menuManager)], // 网格对齐
    [ActionType.GRID_DIALOG, (menuManager, params) => handleChangeGridDialogVisible(menuManager)], // 网格对齐
    [ActionType.OPEN_UNIFORM_FONT, (menuManager, params) => handleQuickTool(params)],
    [ActionType.CHANGE_THEME, (menuManager, params) => handleChangeTheme()],
    [ActionType.OPEN_REPLACE_FONT, (menuManager, params) => handleQuickTool(params)],
    [ActionType.OPEN_MUTI_PICTURE_SIZE_SETTING, (menuManager, params) => handleQuickTool(params)],
    [ActionType.OPEN_MUTI_PICTURE_POS_SETTING, (menuManager, params) => handleQuickTool(params)],
    [ActionType.OPEN_MUTI_PICTURE_EXPORT, (menuManager, params) => handleQuickTool(params)],
    [ActionType.QUICK_TOOL, (menuManager, params) => handleQuickToolShow(params)],
    [ActionType.THEME_EDIT, (menuManager, params) => handleThemeEdit(menuManager)],
    [ActionType.THEME_DELETE, (menuManager, params) => handleThemeDelete(menuManager)],
    [ActionType.TO_SOURCE_URL, (menuManager, params) => handleOpenSourceUrl(menuManager)],
    [ActionType.INSERT_COMMENT, (menuManager, params) => handleInsertComment()],
    [ActionType.MORE_SETTING, (menuManager, params) => handleMoreSetting(menuManager)],
    [ActionType.OPEN_IMAGES_JOINT, (menuManager, params) => handleImagesJoint()],
    [ActionType.AI_REPLACE_SLIDE, (menuManager, params) => handleAIReplaceSlide(params)],
]);

export function handleCommonMenuEvent(menuManagerInstance: IMenuManager, menuEventParams: IMenuEventParams): void {
    const { menuType, actionType, values } = menuEventParams;
    // !!! 企业微信专属功能
    if (IS_ENTERPRISE) {
        // 转交所有权
        menuEventMap.set(ActionType.TRANSFER_OWNERSHIP_WEWORK, _actionTransferOwnership);
        // 融合会议内转发微信会话
        menuEventMap.set(ActionType.FORWARD_DOC_IN_MEETING, forwardDocInTencentMeeting);
        // 微盘上传功能
        // 这里的方案使用的是 C 端空包，不太好维护
        // 后续可以考虑 define-plugin 来消除代码
        WedriveFileManager.setWedriveAction(menuEventMap);
    }
    const matchEvent = menuEventMap.get(actionType);
    if (matchEvent) {
        matchEvent(menuManagerInstance, menuEventParams);
    } else {
        PluginManager.getInstance().handleAction(menuType, actionType, values);
    }
}

export const testObj = {
    _actionSearchReplace,
    _actionCut,
    _actionCopy,
    _actionOpenSelectWindow,
    _actionSelectAll,
    _actionCreateSlide,
    _actionCopySlide,
    _actionDeleteSlide,
    _actionSlidePlay,
    _actionRestartDemo,
    _actionTailorPicNormal,
    _actionChangeBg,
    _actionShapeFormat,
    actionInsertTencentDoc,
    _actionUndo,
    _actionRedo,
    _actionInsertAnimation,
    actionInsertLink,
    actionImportOnlineSlide,
    actionImportLocalSlide,
    _actionInsertIframe,
    handleImportSlideMenuShow,
    actionInsertLatex,
    actionReplaceLatex,
    _actionPaste,
    _actionMoveDown,
    _actionMoveUp,
    _actionMoveTop,
    _actionMoveBottom,
    _actionAddGroup,
    _actionRemoveGroup,
    _actionSlideWide,
    _actionConvertToGroup,
};
